import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ShieldCheck,
  Clock,
  Droplets,
  Home,
  Building,
  Wind,
  Sun,
  Zap,
  CheckCircle,
  Star,
  ArrowRight,
  Leaf,
  Award,
  Target,
  Users,
  Shield,
  Bed,
  Square,
  ExternalLink,
  Phone,
  MessageSquare,
  Calendar,
  MapPin,
  ChevronRight,
  TrendingUp,
  Heart,
  Cloud,
  Thermometer,
  Truck,
  MessageCircle,
  X,
  User,
  Mail,
  Map,
  Check,
  Car, // Added Car icon for car seats cleaning
} from "lucide-react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const ServicesPage = () => {
  const [activeService, setActiveService] = useState(0);
  const navigate = useNavigate();

  // WhatsApp Booking Handler
  const handleWhatsAppBooking = (service) => {
    const phoneNumber = "61420499661";

    const message = `*NEW SERVICE BOOKING REQUEST* 📋

🏢 *Service Details:*
• Service: ${service.title}
• Category: ${service.category || "Cleaning"}

📋 *Service Features:*
${(service.features || [])
  .slice(0, 4)
  .map((feature) => `• ${feature}`)
  .join("\n")}

${service.popular ? "🔥 *MOST POPULAR SERVICE*" : ""}

👤 *I'm interested in booking this service. Please contact me with:*
• Available dates
• Exact pricing
• Time required

💬 *Please reply with your quote and availability.*`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  // Open Booking Page
  const openBookingPage = (service) => {
    navigate(`/booking?service=${service.slug}`);
  };

  // Quick WhatsApp Inquiry
  const handleQuickWhatsApp = (service) => {
    const phoneNumber = "61420499661";

    const message = `*Quick Inquiry - ${service.title}*

Hi, I'm interested in ${service.title} service. 

Please send me:
1. Available dates
2. Pricing for my property size
3. Time required

My location: [Enter Suburb]

Best contact time: [Morning/Afternoon/Evening]`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const services = [
    {
      id: 1,
      icon: Home,
      title: "Residential Cleaning",
      category: "Home Cleaning",
      description:
        "Complete home cleaning for sparkling results. Our residential service covers every corner of your home with attention to detail.",
      features: [
        "Deep Cleaning",
        "Regular Maintenance",
        "Move In/Out",
        "Spring Cleaning",
      ],
      image:
        "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      color: "from-blue-500 to-indigo-600",
      stats: {
        time: "2-4 hours",
        coverage: "All Rooms",
        guarantee: "100% Satisfaction",
      },
      popular: true,
      slug: "residential-cleaning",
    },
    {
      id: 2,
      icon: Building,
      title: "Commercial Cleaning",
      category: "Business Cleaning",
      description:
        "Professional office & workspace solutions. Keep your business environment clean and productive.",
      features: [
        "Daily Service",
        "Custom Plans",
        "After Hours",
        "Disinfection",
      ],
      image:
        "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=80&w=2070&auto=format&fit=crop",
      color: "from-emerald-500 to-teal-600",
      stats: {
        time: "Custom Hours",
        coverage: "Entire Facility",
        guarantee: "Insurance Backed",
      },
      slug: "commercial-cleaning",
    },
    {
      id: 3,
      icon: Droplets,
      title: "Carpet & Upholstery",
      category: "Fabric Cleaning",
      description:
        "Expert cleaning for fabrics & carpets. Remove stains, odors, and allergens with our advanced equipment.",
      features: [
        "Stain Removal",
        "Quick Drying",
        "Odor Elimination",
        "Allergen Reduction",
      ],
      image:
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
      color: "from-amber-500 to-orange-600",
      stats: {
        time: "1-3 hours",
        coverage: "Per Room",
        guarantee: "Stain-Free Promise",
      },
      slug: "carpet-upholstery-cleaning",
    },
    {
      id: 4,
      icon: Wind,
      title: "Duct & Vent Cleaning",
      category: "Air Quality",
      description:
        "Improve indoor air quality with professional duct cleaning. Perfect for allergy sufferers.",
      features: [
        "Air Duct Cleaning",
        "Vent Sanitization",
        "Filter Replacement",
        "Mold Prevention",
      ],
      image:
        "https://images.unsplash.com/photo-1558618883-8c6fc9c5f751?q=80&w=2070&auto=format&fit=crop",
      color: "from-purple-500 to-violet-600",
      stats: {
        time: "3-6 hours",
        coverage: "Full System",
        guarantee: "Air Quality Test",
      },
      slug: "duct-vent-cleaning",
    },
    {
      id: 5,
      icon: Shield,
      title: "End of Lease Cleaning",
      category: "Property Cleaning",
      description:
        "Get your full bond back with our comprehensive end of lease cleaning service.",
      features: [
        "Bond Back Guarantee",
        "Full Property Clean",
        "Professional Report",
        "Same Day Service",
      ],
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      color: "from-rose-500 to-pink-600",
      stats: {
        time: "4-8 hours",
        coverage: "Entire Property",
        guarantee: "Bond Back",
      },
      popular: true,
      slug: "end-of-lease-cleaning",
    },
    {
      id: 6,
      icon: Sun,
      title: "Window & Gutter Cleaning",
      category: "Exterior Cleaning",
      description:
        "Crystal clear windows and clean gutters for improved curb appeal and home maintenance.",
      features: [
        "Interior/Exterior",
        "Gutter Clearing",
        "Screen Cleaning",
        "Hard Water Stain Removal",
      ],
      image:
        "https://images.unsplash.com/photo-1628083333913-f2cc3d1b7f9c?q=80&w=2070&auto=format&fit=crop",
      color: "from-cyan-500 to-blue-600",
      stats: {
        time: "1-2 hours",
        coverage: "All Windows",
        guarantee: "Streak-Free",
      },
      slug: "window-gutter-cleaning",
    },
    {
      id: 7,
      icon: Zap,
      title: "Emergency Restoration",
      category: "Emergency Services",
      description:
        "24/7 emergency services for water damage, flood recovery, and disaster cleanup.",
      features: [
        "Water Extraction",
        "Mold Remediation",
        "Structural Drying",
        "Insurance Claims",
      ],
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      color: "from-red-500 to-orange-600",
      stats: {
        time: "Immediate",
        coverage: "Emergency Response",
        guarantee: "Insurance Ready",
      },
      slug: "emergency-restoration",
    },
    {
      id: 8,
      icon: Sparkles,
      title: "Deep Cleaning",
      category: "Specialized Cleaning",
      description:
        "Intensive cleaning for special occasions, renovations, or seasonal deep cleans.",
      features: [
        "Detailed Sanitization",
        "Grout Cleaning",
        "Appliance Cleaning",
        "Fixture Polishing",
      ],
      image:
        "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?q=80&w=2070&auto=format&fit=crop",
      color: "from-indigo-500 to-purple-600",
      stats: {
        time: "5-8 hours",
        coverage: "Complete Home",
        guarantee: "Deep Clean Guarantee",
      },
      slug: "deep-cleaning",
    },
    {
      id: 9,
      icon: Square,
      title: "Rug Cleaning",
      category: "Fabric Cleaning",
      description:
        "Professional cleaning for area rugs and oriental carpets. Special care for delicate fibers and antique pieces.",
      features: [
        "Hand Washing",
        "Stain Treatment",
        "Fringe Cleaning",
        "Gentle Drying",
      ],
      image:
        "https://images.unsplash.com/photo-1575428652377-a2d80e2277fc?q=80&w=2070&auto=format&fit=crop",
      color: "from-amber-600 to-yellow-600",
      stats: {
        time: "3-5 hours",
        coverage: "Per Rug",
        guarantee: "Color Protection",
      },
      popular: true,
      slug: "rug-cleaning",
    },
    {
      id: 10,
      icon: Bed,
      title: "Mattress Cleaning",
      category: "Health & Wellness",
      description:
        "Deep sanitization to remove allergens, dust mites, and stains for a healthier sleep environment.",
      features: [
        "Allergen Removal",
        "Stain Treatment",
        "Sanitization",
        "Deodorizing",
      ],
      image:
        "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=2070&auto=format&fit=crop",
      color: "from-purple-600 to-pink-600",
      stats: {
        time: "1-2 hours",
        coverage: "Per Mattress",
        guarantee: "Allergen Free",
      },
      slug: "mattress-cleaning",
    },
    {
      id: 11,
      icon: Square,
      title: "Rugs Deep Cleaning",
      category: "Fabric Cleaning",
      description:
        "Comprehensive deep cleaning service for all types of rugs, including high-traffic area restoration.",
      features: [
        "Deep Extraction",
        "pH-Balanced Solutions",
        "Mold Prevention",
        "Texture Restoration",
      ],
      image:
        "https://images.unsplash.com/photo-1531835551805-16d864c8d311?q=80&w=2070&auto=format&fit=crop",
      color: "from-teal-600 to-emerald-600",
      stats: {
        time: "2-4 hours",
        coverage: "All Rugs",
        guarantee: "Deep Clean Guarantee",
      },
      slug: "rugs-deep-cleaning",
    },
    {
      id: 12,
      icon: Square,
      title: "Curtain Cleaning",
      category: "Fabric Cleaning",
      description:
        "Professional curtain cleaning service that removes dust, stains, and allergens while preserving fabric quality and color.",
      features: [
        "On-Site Cleaning",
        "Fabric-Safe Solutions",
        "Dust & Allergen Removal",
        "Wrinkle & Odor Reduction",
      ],
      image:
        "https://images.unsplash.com/photo-1582582429416-1c3d26c36b7f?q=80&w=2070&auto=format&fit=crop",
      color: "from-teal-600 to-emerald-600",
      stats: {
        time: "1-3 hours",
        coverage: "All Curtain Types",
        guarantee: "Freshness Guarantee",
      },
      slug: "curtain-cleaning",
    },
    // NEW: Car Seats Cleaning Service
    {
      id: 13,
      icon: Car,
      title: "Car Seats Cleaning",
      category: "Automotive Cleaning",
      description:
        "Professional deep cleaning for your vehicle's upholstery. Remove stains, odors, and allergens from fabric or leather car seats.",
      features: [
        "Fabric & Leather Cleaning",
        "Stain & Odor Removal",
        "Allergen Elimination",
        "Sanitization Treatment",
      ],
      image:
        "https://images.unsplash.com/photo-1581548731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      color: "from-slate-500 to-gray-700",
      stats: {
        time: "1-2 hours",
        coverage: "All Seats",
        guarantee: "Freshness Guarantee",
      },
      popular: false,
      slug: "car-seats-cleaning",
    },
  ];

  const specialties = [
    {
      icon: ShieldCheck,
      title: "Insured & Bonded",
      description: "$2M liability insurance for peace of mind",
    },
    {
      icon: Leaf,
      title: "Eco-Friendly",
      description: "Green cleaning products safe for families & pets",
    },
    {
      icon: Award,
      title: "Certified",
      description: "Industry certified technicians with extensive training",
    },
    {
      icon: Target,
      title: "Precision",
      description: "Attention to detail in every corner and surface",
    },
    {
      icon: Users,
      title: "Trained Team",
      description: "Background checked and professionally trained staff",
    },
    {
      icon: Clock,
      title: "24/7 Emergency",
      description: "Round-the-clock service for urgent situations",
    },
  ];

  const pricingPlans = [
    {
      name: "Basic",
      price: "$129",
      period: "per room",
      features: [
        "Standard cleaning",
        "Surface sanitization",
        "Vacuum & mop",
        "Bathroom cleaning",
        "Kitchen cleaning",
      ],
      color: "from-blue-400 to-blue-600",
      popular: false,
    },
    {
      name: "Premium",
      price: "$249",
      period: "per session",
      features: [
        "Deep cleaning",
        "Appliance cleaning",
        "Window interior",
        "Cabinets & fixtures",
        "Eco-friendly products",
        "Priority booking",
      ],
      color: "from-indigo-500 to-purple-600",
      popular: true,
    },
    {
      name: "Commercial",
      price: "Custom",
      period: "monthly plan",
      features: [
        "Daily/Weekly service",
        "Custom schedule",
        "After hours",
        "Specialized equipment",
        "Monthly reports",
        "Dedicated team",
      ],
      color: "from-emerald-500 to-teal-600",
      popular: false,
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Homeowner, Glen Waverley",
      content:
        "Knight Cleanings transformed our home! The end of lease cleaning was so thorough we got our full bond back. Professional, punctual, and perfect results.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Business Owner, Dandenong",
      content:
        "Our office has never looked better. The commercial cleaning team works around our schedule and leaves everything spotless every time.",
      rating: 5,
    },
    {
      name: "Emma Roberts",
      role: "Resident, Mornington",
      content:
        "The mattress cleaning service was a game changer for my allergies. They were professional, efficient, and my mattress looks and feels brand new!",
      rating: 5,
    },
  ];

  const areasServed = [
    "Melbourne CBD",
    "Dandenong",
    "Hallam",
    "Hampton Park",
    "Clayton",
    "Keysborough",
    "Glen Waverley",
    "Mount Waverley",
    "Noble Park",
    "Lynbrook",
    "Doveton",
    "Mornington",
    "Rye",
    "Rosebud",
    "Frankston",
    "All Surrounding Suburbs",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Our Cleaning Services in Melbourne VIC</title>
        <meta
          name="description"
          content="Explore our professional cleaning services in Melbourne VIC, including residential, commercial, deep cleaning, and restoration."
        />
        <meta
          name="keywords"
          content="Carpet cleaning Melbourne, Upholstery cleaning Melbourne, End of lease cleaning Melbourne, Mattress cleaning Melbourne, Professional cleaning Melbourne, Car seats cleaning Melbourne"
        />
        <meta name="robots" content="index, follow" />
        <meta
          name="author"
          content="Professional Cleaning Services Melbourne"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Professional Cleaning Services in Melbourne",
            url: "https://knightcleanings.com.au/services",
            provider: {
              "@type": "CleaningService",
              name: "Knight Cleaning",
              telephone: "+61420499661",
              address: {
                "@type": "PostalAddress",
                streetAddress: "11 Warath Drive, Dandenong North",
                addressLocality: "Melbourne",
                addressRegion: "VIC",
                postalCode: "3175",
                addressCountry: "AU",
              },
            },
            areaServed: [
              { "@type": "City", name: "Melbourne" },
              { "@type": "City", name: "Dandenong" },
              { "@type": "City", name: "Hallam" },
              { "@type": "City", name: "Hampton Park" },
              { "@type": "City", name: "Clayton" },
              { "@type": "City", name: "Keysborough" },
              { "@type": "City", name: "Glen Waverley" },
              { "@type": "City", name: "Mount Waverley" },
              { "@type": "City", name: "Noble Park" },
              { "@type": "City", name: "Lynbrook" },
              { "@type": "City", name: "Doveton" },
              { "@type": "AdministrativeArea", name: "Mornington Peninsula" },
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute h-2 w-2 rounded-full bg-white animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles size={18} />
              <span className="font-semibold">Premium Cleaning Solutions</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Our{" "}
              <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">
              Comprehensive Cleaning Solutions for Melbourne
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Experience professional cleaning services that transform your
              space with precision and care. Serving Melbourne and surrounding
              suburbs since 2010.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() =>
                  window.scrollTo({
                    top: document.getElementById("services").offsetTop,
                    behavior: "smooth",
                  })
                }
                className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-2"
              >
                <span>View All Services</span>
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => navigate("/booking")}
                className="border-2 border-white/30 hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold px-8 py-3 rounded-full transition-all duration-300 flex items-center gap-2"
              >
                <Calendar size={18} />
                <span>Book Now</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "5000+", label: "Happy Customers", icon: Users },
              { value: "13", label: "Services", icon: Award },
              { value: "98%", label: "Satisfaction Rate", icon: Heart },
              { value: "24/7", label: "Emergency Service", icon: Clock },
            ].map((stat, index) => (
              <div key={index} className="text-center group cursor-pointer">
                <div className="flex justify-center mb-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <stat.icon size={24} className="text-blue-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Showcase */}
      <section
        id="services"
        className="py-16 bg-gradient-to-b from-gray-50 to-white"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Cleaning Services</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to your specific needs
              and schedule. Book directly via WhatsApp for instant confirmation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${activeService === index ? "ring-2 ring-blue-500" : ""}`}
                onMouseEnter={() => setActiveService(index)}
              >
                {/* Service Header */}
                <div className="relative h-48 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 hover:scale-110"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  <div
                    className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-70`}
                  />
                  {service.popular && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4">
                    <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <service.icon size={24} className="text-white" />
                    </div>
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full mb-2 inline-block">
                        {service.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{service.description}</p>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      SERVICE INCLUDES:
                    </h4>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle
                            size={16}
                            className="text-green-500 flex-shrink-0"
                          />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-6">
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-sm font-semibold text-gray-900">
                        {service.stats.time}
                      </div>
                      <div className="text-xs text-gray-600">Time</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-sm font-semibold text-gray-900">
                        {service.stats.coverage}
                      </div>
                      <div className="text-xs text-gray-600">Coverage</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-sm font-semibold text-gray-900">
                        {service.stats.guarantee}
                      </div>
                      <div className="text-xs text-gray-600">Guarantee</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => openBookingPage(service)}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <span>Book This Service</span>
                      <ArrowRight size={18} />
                    </button>

                    <button
                      onClick={() => handleWhatsAppBooking(service)}
                      className="w-full border-2 border-green-600 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={18} />
                      <span>Instant WhatsApp Booking</span>
                    </button>

                    <Link
                      to={`/services/${service.slug}`}
                      className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <span>View Details</span>
                      <ExternalLink size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-blue-600">Knight Cleanings</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We combine professional expertise with cutting-edge technology to
              deliver exceptional cleaning results that exceed expectations.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialties.map((specialty, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="h-14 w-14 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <specialty.icon size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {specialty.title}
                </h3>
                <p className="text-gray-600">{specialty.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Transparent <span className="text-blue-600">Pricing</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              No hidden fees. Choose the plan that fits your needs and budget.
              All plans include our satisfaction guarantee.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${plan.popular ? "ring-2 ring-amber-300 relative" : ""}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-full text-sm">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-gray-600">/{plan.period}</span>
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    WHAT'S INCLUDED:
                  </h4>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle
                          size={18}
                          className="text-green-500 flex-shrink-0"
                        />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() =>
                    openBookingPage({
                      slug: "custom-quote",
                      title: `${plan.name} Plan`,
                      category: plan.name,
                    })
                  }
                  className={`w-full bg-gradient-to-r ${plan.color} hover:opacity-90 text-white font-bold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02]`}
                >
                  Select Plan
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-600">
              Need a custom quote?{" "}
              <button
                onClick={() =>
                  handleWhatsAppBooking({
                    title: "Custom Quote Request",
                    features: [
                      "Custom pricing",
                      "Flexible schedule",
                      "Tailored services",
                    ],
                    category: "Custom Quote",
                  })
                }
                className="text-blue-600 font-semibold hover:underline"
              >
                Contact us via WhatsApp
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* WhatsApp Quick Contact */}
      <section className="py-12 bg-gradient-to-r from-green-500 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle size={32} className="text-white" />
                <h3 className="text-2xl font-bold">Instant WhatsApp Support</h3>
              </div>
              <p className="text-green-100">
                Get instant quotes, schedule services, or ask questions directly
                via WhatsApp.
              </p>
            </div>
            <button
              onClick={() =>
                handleWhatsAppBooking({
                  title: "General Inquiry",
                  features: ["Quick response", "Instant quote", "Easy booking"],
                  category: "General Inquiry",
                })
              }
              className="bg-white text-green-700 hover:bg-green-50 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-2"
            >
              <MessageCircle size={20} />
              <span>Chat on WhatsApp Now</span>
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-blue-600">Clients Say</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied
              customers have to say about our services.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={20}
                      className="text-amber-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Process</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Simple steps from booking to sparkling clean results.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Book Online",
                description: "Schedule your service in minutes",
                icon: Calendar,
                color: "from-blue-500 to-blue-600",
              },
              {
                step: "02",
                title: "Free Estimate",
                description: "Get a transparent quote",
                icon: MessageSquare,
                color: "from-emerald-500 to-emerald-600",
              },
              {
                step: "03",
                title: "Professional Service",
                description: "Certified team arrives on time",
                icon: Users,
                color: "from-purple-500 to-purple-600",
              },
              {
                step: "04",
                title: "Quality Check",
                description: "100% satisfaction guarantee",
                icon: ShieldCheck,
                color: "from-amber-500 to-amber-600",
              },
            ].map((step, index) => (
              <div key={step.step} className="text-center">
                <div className="relative mb-6">
                  <div
                    className={`h-20 w-20 rounded-full bg-gradient-to-r ${step.color} mx-auto flex items-center justify-center text-white text-2xl font-bold mb-4`}
                  >
                    <step.icon size={32} />
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-gray-200 to-gray-100" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Areas <span className="text-blue-600">We Serve</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We proudly serve Melbourne and all surrounding suburbs. Contact us
              via WhatsApp to check availability in your area.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {areasServed.map((area, index) => (
              <button
                key={index}
                onClick={() =>
                  handleWhatsAppBooking({
                    title: `Service Inquiry - ${area}`,
                    features: [
                      "Local service",
                      "Same-day availability",
                      "Free quote",
                    ],
                    category: "Area Inquiry",
                  })
                }
                className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200 hover:border-green-300 hover:shadow-md transition-all duration-300 text-center group"
              >
                <div className="flex flex-col items-center gap-2">
                  <MapPin
                    size={20}
                    className="text-blue-600 group-hover:text-green-600"
                  />
                  <span className="font-medium text-gray-900 group-hover:text-green-700">
                    {area}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600">
              Don't see your suburb?{" "}
              <button
                onClick={() =>
                  handleWhatsAppBooking({
                    title: "Area Availability Check",
                    features: [
                      "New suburb service",
                      "Travel quote",
                      "Custom arrangement",
                    ],
                    category: "Area Check",
                  })
                }
                className="text-blue-600 font-semibold hover:underline"
              >
                WhatsApp us to check availability
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional <span className="text-blue-600">Equipment</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We use state-of-the-art equipment and eco-friendly products to
              deliver superior cleaning results.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Advanced Steam Cleaners",
                description: "Hospital-grade sanitization",
                icon: Thermometer,
                color: "from-blue-500 to-cyan-500",
              },
              {
                title: "HEPA Filtration",
                description: "Allergen and dust removal",
                icon: Cloud,
                color: "from-emerald-500 to-teal-500",
              },
              {
                title: "Truck-Mounted Systems",
                description: "Powerful extraction",
                icon: Truck,
                color: "from-purple-500 to-pink-500",
              },
              {
                title: "Eco-Friendly Solutions",
                description: "Safe for families & pets",
                icon: Leaf,
                color: "from-amber-500 to-orange-500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300"
              >
                <div
                  className={`h-16 w-16 rounded-2xl bg-gradient-to-r ${item.color} mx-auto flex items-center justify-center mb-4`}
                >
                  <item.icon size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Star size={18} />
            <span className="font-semibold">Limited Time Offer</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Get 20% Off Your First Service!
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Book now and experience the Knight Cleanings difference.
            Satisfaction guaranteed or your money back.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate("/booking")}
              className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 text-lg flex items-center justify-center gap-2"
            >
              <Calendar size={20} />
              <span>Book Online Now</span>
            </button>
            <button
              onClick={() =>
                handleWhatsAppBooking({
                  title: "First Time Discount",
                  features: [
                    "20% discount",
                    "Professional service",
                    "Satisfaction guarantee",
                  ],
                  category: "Discount Offer",
                })
              }
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 text-lg flex items-center justify-center gap-2"
            >
              <MessageCircle size={20} />
              <span>WhatsApp for Discount</span>
            </button>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: "24/7", label: "Emergency Service" },
              { value: "100%", label: "Satisfaction" },
              { value: "Same Day", label: "Available" },
              { value: "Free", label: "Quotes" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl font-bold">{item.value}</div>
                <div className="text-sm text-blue-200">{item.label}</div>
              </div>
            ))}
          </div>
          <p className="text-blue-200 mt-6 text-sm">
            Offer valid for first-time customers only. Terms apply.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-blue-600">Questions</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get answers to common questions about our cleaning services.
            </p>
          </div>
          <div className="space-y-6">
            {[
              {
                question: "How do I book a cleaning service?",
                answer:
                  "You can book online through our booking page, call us directly at +61 420 499 661, or message us on WhatsApp for instant booking. We'll schedule a convenient time for your cleaning service.",
              },
              {
                question:
                  "Are your cleaning products safe for pets and children?",
                answer:
                  "Yes! We use eco-friendly, non-toxic cleaning products that are safe for pets, children, and people with allergies. All our products are certified safe for residential use.",
              },
              {
                question: "Do I need to be home during the cleaning?",
                answer:
                  "No, you don't need to be present. We can work with your schedule and access instructions. Many of our clients provide us with keys or access codes for convenience.",
              },
              {
                question: "What's included in your bond cleaning service?",
                answer:
                  "Our bond cleaning includes comprehensive cleaning of the entire property, carpet steam cleaning, window cleaning, oven deep cleaning, and a detailed checklist of over 100 items to ensure you get your full bond back.",
              },
              {
                question: "Do you offer same-day service?",
                answer:
                  "Yes, we offer same-day service for emergency situations and urgent bookings. Contact us via WhatsApp as early as possible to check same-day availability.",
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">Q</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600">
              Have more questions?{" "}
              <button
                onClick={() =>
                  handleWhatsAppBooking({
                    title: "General Question",
                    features: [
                      "Quick answer",
                      "Professional advice",
                      "Service information",
                    ],
                    category: "Question",
                  })
                }
                className="text-blue-600 font-semibold hover:underline"
              >
                WhatsApp our support team
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() =>
            handleWhatsAppBooking({
              title: "General Inquiry",
              features: ["Quick response", "Instant quote", "Easy booking"],
              category: "General",
            })
          }
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 animate-bounce-slow flex items-center justify-center"
        >
          <MessageCircle size={28} />
        </button>
      </div>
    </div>
  );
};

export default ServicesPage;